// Copyright (c) Docugami, Inc. All rights reserved.

import { useCallback, useMemo } from "react";
import { isValidDecodedTag } from "utils/TagStandardUtils";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { useAppSelector } from "./redux";
import { selectLabelState } from "store/frontend/selectors";
import { CreateDocumentPropertyRequest, PropertyInstance, LabelSchema, selectAllProperties, useCreateDocumentPropertyMutation, useListDocumentPropertiesQuery, useSetPropertyValueMutation, useRenameLabelMutation } from "store/frontend/frontendSlice";
import { folderIdSelector } from "store/dgml/dgmlSelectors";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { ChunkInstance, TokenRange } from "networking/api";
import { FolderId } from "components/file_management/file_helpers";

export type Property = PropertyInstance & {
    docsetId: string;
    spanId: string;
    chunk?: ChunkInstance;
}

interface UpdatePropertyInput {
    label?: string;
    value?: string;
}

interface IDocumentProperties {
    properties: Property[];
    isLoading: boolean;
    validate: (label: string, value: string) => boolean;
    addProperty: (label: string, value: string, tokenRanges?: TokenRange[]) => Promise<Property | undefined>;
    updateProperty: (targetId: string, input: UpdatePropertyInput) => Promise<void>;
}

export function useDocumentProperties(documentId: string): IDocumentProperties {
    const folderId = folderIdSelector(documentId);
    const { data: propertiesData, isLoading: isPropertiesLoading } = useListDocumentPropertiesQuery(
        !!folderId ? { docsetId: folderId.clusterId, fileId: folderId.fileId } : skipToken);

    const { data: labelState } = useAppSelector(state => selectLabelState(folderId?.clusterId ?? '')(state));

    const [createProperty] = useCreateDocumentPropertyMutation();
    const [renameLabel] = useRenameLabelMutation();
    const [setValue] = useSetPropertyValueMutation();

    const properties = useMemo<Property[]>(() => {
        if (!!propertiesData && !!labelState) {
            return selectAllProperties(propertiesData).map(x => {
                const label = labelState.entities[x.labelId];
                return createPropertyHelper(x, folderId!, label)
            });
        }

        return [];
    }, [labelState, propertiesData, folderId]);

    const validate = useCallback((label: string, value: string): boolean => {
        if (!label || !value) {
            return false;
        }

        if (!labelState) {
            return false;
        }

        if (!isValidDecodedTag(label)) {
            return false;
        }

        if (value.length > 250) {
            return false;
        }

        return true;
    }, [labelState]);

    const addProperty = useCallback(async (label: string, value: string, tokenRanges?: TokenRange[]): Promise<Property | undefined> => {
        if (!labelState) {
            return;
        }

        label = label.trim();

        const payload: CreateDocumentPropertyRequest = {
            label: label,
            value: value,
            tokenRanges: tokenRanges
        };

        const newProperty = await createProperty({
            docsetId: folderId!.clusterId,
            fileId: folderId!.fileId,
            body: payload
        }).unwrap();

        return createPropertyHelper(newProperty, folderId!, labelState.entities[newProperty.labelId])
    }, [createProperty, folderId, labelState]);

    const updateProperty = useCallback(async (targetSpanId: string, input: UpdatePropertyInput) => {
        const property = properties.find(x => x.spanId === targetSpanId);
        if (!property || !folderId) {
            return Promise.resolve();
        }

        const updates = [];
        if (property.value !== input.value) {
            updates.push(setValue({
                docsetId: folderId!.clusterId,
                fileId: folderId.fileId,
                propertyId: property.id,
                body: {
                    value: input.value
                }
            }))
        }

        if (property.label !== input.label && !!input.label && !!property.labelId) {
            updates.push(renameLabel({
                docsetId: folderId.clusterId,
                labelId: property.labelId,
                label: input.label.trim()
            }));
        }

        await Promise.allSettled(updates);
    }, [properties, folderId, setValue, renameLabel]);

    return {
        properties: properties,
        isLoading: isPropertiesLoading || !labelState,
        validate: validate,
        addProperty: addProperty,
        updateProperty: updateProperty
    }
}

const createPropertyHelper = (property: PropertyInstance, folderId: FolderId, label: LabelSchema | undefined): Property => {
    const spanId = SpanIdUtils.createAddedChunk(folderId.fileId, property.id).value
    return {
        ...property,
        docsetId: folderId!.clusterId,
        label: label?.label ?? property.label,
        spanId: spanId,
        chunk: ({
            chunkInstanceId: spanId,
            spanId: spanId,
            labelId: property.labelId,
            folderId: folderId!.value,
            isAddedChunk: true,
            tokenRange: { fromTok: 0, toTok: 0 },
            chunk: {
                docsetId: folderId!.clusterId,
                fileId: folderId!.fileId,
                spanId: spanId,
                tokenRange: { fromTok: 0, toTok: 0 },
                tokenKeyRange: { fromTokenKey: '0', toTokenKey: '0' },
                labels: [],
                children: []
            }
        })
    };
}
